/**
 * SketchManager — the team-side editor for a journey "Sketch ribbon" entry's data.sketches.
 * Upload images OR PDFs, set per-sketch title / type / caption, reorder, delete. Bound to the
 * entry's data.sketches array via value/onChange (the JourneyEntryModal persists it on save).
 * While the entry is an unpublished draft, none of this is visible to the client.
 *
 * A PDF gets almost the same treatment as a Concept Framework/IDP/ICP presentation's Sketches wall
 * (SketchWall.jsx#addFile): its first page becomes the ribbon thumbnail and the PDF itself is
 * uploaded to Supabase via uploadConceptDrawing (services/conceptDrawings.js). Unlike that flow,
 * this one does NOT auto-open ConceptDrawingModal for calibration on upload — this manager lives
 * INSIDE JourneyEntryModal (a blocking, higher-z-index overlay), and the sketch's entry doesn't
 * exist as a saved row yet while the Add/Edit modal is still open, so popping the tool here rendered
 * BEHIND the modal and calibrating before Save existed nowhere to persist to (2026-07 bug report).
 * Calibration instead happens after the entry is saved — as a DRAFT (see JourneyEntryModal's
 * "Publish immediately" checkbox) — via the outline's own per-sketch "Open drawing tool" action
 * (JourneyAuthorSection.jsx#SketchItemRow), the same fix path already built for drawings uploaded
 * before that popup ever worked reliably. The ribbon item is flagged `isDrawing: true` so
 * SketchRibbon.jsx opens the measure viewer instead of a plain lightbox once published. Unlike
 * presentation sketches (Airtable-backed, keyed by a Concept Sketches record id), a ribbon item has
 * no Airtable record at all — `concept_drawings.sketch_airtable_id` is a plain text column with no
 * format check, so a locally-generated key works just as well.
 */

import { useRef, useState } from 'react'
import { uploadImage } from '../services/uploads.js'
import { uploadConceptDrawing } from '../services/conceptDrawings.js'
import { usePdfPagePicker } from '../components/PdfPagePickerModal.jsx'
import { renderPdfThumbnail } from '../present/SketchWall.jsx'

const TYPES = ['', 'Site Plan', 'Floor Plan', 'Exterior Elevation', 'Interior Elevation',
  'Building Section', 'Roof Plan', 'Perspective', 'Rendering', 'Diagram', 'Detail', 'Other']

const IS_PDF = f => /\.pdf$/i.test(f?.name || '') || f?.type === 'application/pdf'
const newDrawingKey = () => 'sk_' + Date.now().toString(36) + Math.random().toString(36).slice(2, 7)

export default function SketchManager({ value, onChange, projectId }) {
  const list = Array.isArray(value) ? value : []
  const fileRef = useRef(null)
  const [busy, setBusy] = useState(false)
  const [err, setErr] = useState('')
  const { resolveUpload, picker } = usePdfPagePicker()

  const update = (i, patch) => onChange(list.map((s, j) => (j === i ? { ...s, ...patch } : s)))
  const remove = (i) => onChange(list.filter((_, j) => j !== i))
  const move = (i, d) => {
    const n = i + d; if (n < 0 || n >= list.length) return
    const c = list.slice(); const t = c[i]; c[i] = c[n]; c[n] = t; onChange(c)
  }

  async function add(e) {
    const files = Array.from(e.target.files || []); if (fileRef.current) fileRef.current.value = ''
    if (!files.length) return
    setBusy(true); setErr('')
    const added = []
    for (const f of files) {
      try {
        if (IS_PDF(f)) {
          const resolved = await resolveUpload(f)   // page-picker if multi-page; null if cancelled
          if (!resolved) continue
          const thumbCanvas = await renderPdfThumbnail(resolved)
          const thumbBlob = await new Promise(res => thumbCanvas.toBlob(res, 'image/png'))
          const niceTitle = (f.name || 'Sketch').replace(/\.pdf$/i, '')
          const { url } = await uploadImage(thumbBlob, niceTitle + '.png')
          const key = newDrawingKey()
          await uploadConceptDrawing(key, projectId, resolved, thumbBlob)
          added.push({ id: key, url, title: niceTitle, type: '', legend: '', isDrawing: true })
          // No auto-open here — see the file header comment. Calibrate from the outline's "Open
          // drawing tool" action after saving (as a draft).
        } else {
          const { url } = await uploadImage(f, f.name)
          added.push({ id: url, url, title: '', type: '', legend: '' })
        }
      } catch (ex) { setErr(ex.message || 'Upload failed') }
    }
    if (added.length) onChange([...list, ...added])
    setBusy(false)
  }

  return (
    <div style={{ border: '0.5px dashed rgba(7,39,45,0.22)', borderRadius: 6, padding: 10, background: '#f9fbfc' }}>
      {list.map((s, i) => (
        <div key={s.id || i} style={{ display: 'flex', gap: 8, alignItems: 'flex-start', padding: '8px 0', borderBottom: '0.5px solid rgba(7,39,45,0.07)' }}>
          <div style={{ position: 'relative', flexShrink: 0 }}>
            <img src={s.url} alt="" style={{ width: 54, height: 42, objectFit: 'cover', borderRadius: 3, border: '0.5px solid rgba(7,39,45,0.12)', display: 'block' }} />
            {s.isDrawing && (
              <span style={{ position: 'absolute', bottom: 2, right: 2, fontFamily: '"DM Mono",monospace', fontSize: 7, fontWeight: 700, letterSpacing: '0.04em', textTransform: 'uppercase', color: '#fff', background: 'rgba(7,39,45,0.82)', padding: '1px 4px', borderRadius: 2 }}>
                Drawing
              </span>
            )}
          </div>
          <div style={{ flex: 1, display: 'flex', flexDirection: 'column', gap: 4, minWidth: 0 }}>
            <input value={s.title || ''} onChange={e => update(i, { title: e.target.value })} placeholder="Title (optional)" style={mini} />
            <div style={{ display: 'flex', gap: 4 }}>
              <select value={s.type || ''} onChange={e => update(i, { type: e.target.value })} style={{ ...mini, flex: '0 0 132px' }}>
                {TYPES.map(t => <option key={t} value={t}>{t || 'Type…'}</option>)}
              </select>
              <input value={s.legend || ''} onChange={e => update(i, { legend: e.target.value })} placeholder="Caption (optional)" style={{ ...mini, flex: 1 }} />
            </div>
          </div>
          <div style={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
            <button type="button" onClick={() => move(i, -1)} disabled={i === 0} style={icon} title="Move up">↑</button>
            <button type="button" onClick={() => move(i, 1)} disabled={i === list.length - 1} style={icon} title="Move down">↓</button>
            <button type="button" onClick={() => remove(i)} style={{ ...icon, color: '#c0392b' }} title="Remove">×</button>
          </div>
        </div>
      ))}
      <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginTop: 8 }}>
        <button type="button" onClick={() => fileRef.current?.click()} disabled={busy}
          style={{ fontFamily: '"DM Sans",sans-serif', fontSize: 11, color: '#2563eb', background: 'none', border: 'none', cursor: busy ? 'default' : 'pointer', padding: 0 }}>
          {busy ? 'Uploading…' : '+ Add sketches'}
        </button>
        {err && <span style={{ fontFamily: '"DM Sans",sans-serif', fontSize: 11, color: '#c0392b' }}>{err}</span>}
      </div>
      <input ref={fileRef} type="file" accept="image/*,application/pdf" multiple style={{ display: 'none' }} onChange={add} />
      {picker}
      <div style={{ fontFamily: '"DM Sans",sans-serif', fontSize: 10, color: '#9bb2b7', marginTop: 4 }}>
        Shown to the client as a vertical gallery once this entry is published. A PDF opens as a
        scaled drawing with measure tools instead of a plain image, the same as a Concept
        Framework/IDP/ICP presentation sketch — save this entry as a draft first, then set its
        scale from the outline's "Open drawing tool" action before publishing.
      </div>
    </div>
  )
}

const mini = { width: '100%', padding: '4px 7px', fontFamily: '"DM Sans",sans-serif', fontSize: 11, color: '#07272D', background: '#fff', border: '0.5px solid rgba(7,39,45,0.18)', borderRadius: 3, boxSizing: 'border-box', outline: 'none' }
const icon = { background: 'none', border: '0.5px solid rgba(7,39,45,0.18)', borderRadius: 3, cursor: 'pointer', fontSize: 11, color: '#3a5260', width: 20, height: 18, lineHeight: 1, padding: 0 }
